import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Phone, Building, MapPin, Globe, 
  Upload, Download, Trash2, StickyNote, Plus, Linkedin, Twitter, 
  Facebook, Instagram, FileText, UserPlus, Music, Radio, Link
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import PDFViewer from '../ui/PDFViewer';

const LeadDetails = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leadData, setLeadData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [editData, setEditData] = useState({});
  const [uploadData, setUploadData] = useState({
    file: null,
    tags: [],
    customTag: ''
  });
  const [predefinedTags, setPredefinedTags] = useState([]);

  useEffect(() => {
    fetchLeadDetails();
    fetchPredefinedTags();
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      const response = await api.get(`/leads/${leadId}/details`);
      setLeadData(response.data);
      setEditData(response.data.lead);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPredefinedTags = async () => {
    try {
      const response = await api.get('/files/tags/all');
      setPredefinedTags(response.data.predefined || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData({ ...uploadData, file });
      setUploadDialogOpen(true);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    
    // Add custom tag if provided
    let allTags = [...uploadData.tags];
    if (uploadData.customTag.trim()) {
      allTags.push(uploadData.customTag.trim());
    }
    if (allTags.length > 0) {
      formData.append('tags', JSON.stringify(allTags));
    }

    try {
      await api.post(`/leads/${leadId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully!');
      setUploadDialogOpen(false);
      setUploadData({ file: null, tags: [], customTag: '' });
      fetchLeadDetails();
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setTextContent('');
    setPreviewUrl('');
    
    try {
      // For text files, fetch content as text
      if (file.mime_type === 'text/plain') {
        const response = await api.get(`/leads/${leadId}/documents/${file.id}/preview`, {
          responseType: 'text'
        });
        setTextContent(response.data);
      } 
      // For PDFs, use direct URL with token for iframe rendering
      else if (file.mime_type === 'application/pdf') {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const directUrl = `${backendUrl}/api/leads/${leadId}/documents/${file.id}/preview?token=${token}`;
        setPreviewUrl(directUrl);
      }
      // For images, fetch as blob and create object URL
      else if (file.mime_type.startsWith('image/')) {
        const response = await api.get(`/leads/${leadId}/documents/${file.id}/preview`, {
          responseType: 'blob'
        });
        // Create blob with correct MIME type
        const blob = new Blob([response.data], { type: file.mime_type });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Failed to load file for preview:', error);
      toast.error('Failed to load file preview');
    }
    
    setPreviewDialogOpen(true);
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await api.get(`/leads/${leadId}/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/leads/${leadId}/documents/${docId}`);
      toast.success('Document deleted successfully!');
      fetchLeadDetails();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await api.post(`/leads/${leadId}/notes`, {
        lead_id: leadId,
        note: newNote
      });
      toast.success('Note added successfully!');
      setNewNote('');
      setNoteDialogOpen(false);
      fetchLeadDetails();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await api.delete(`/leads/${leadId}/notes/${noteId}`);
      toast.success('Note deleted!');
      fetchLeadDetails();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/leads/${leadId}`, editData);
      toast.success('Lead updated successfully!');
      setEditDialogOpen(false);
      fetchLeadDetails();
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleConvertToClient = async () => {
    if (!window.confirm('Convert this lead to a client? All data will be transferred.')) return;

    try {
      const response = await api.post(`/leads/${leadId}/convert`);
      toast.success(`Lead converted! ${response.data.transferred_notes} notes and ${response.data.transferred_documents} documents transferred.`);
      navigate(`/admin/clients/${response.data.client_id}`);
    } catch (error) {
      console.error('Failed to convert lead:', error);
      toast.error('Failed to convert lead');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getSocialIcon = (platform) => {
    const icons = {
      linkedin: <Linkedin className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      instagram: <Instagram className="w-4 h-4" />,
      tiktok: <Music className="w-4 h-4" />,
      podcast: <Radio className="w-4 h-4" />,
      custom_link: <Link className="w-4 h-4" />
    };
    return icons[platform.toLowerCase()] || <Globe className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Lead not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/leads')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Leads
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-testid="lead-name">
              {leadData.lead.first_name} {leadData.lead.last_name}
            </h2>
            <div className="flex items-center space-x-4 text-sm">
              <span className={`badge ${leadData.lead.status === 'converted' ? 'badge-success' : 'badge-info'}`}>
                {leadData.lead.status}
              </span>
              {leadData.lead.company_name && (
                <span className="text-gray-600">{leadData.lead.company_name}</span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="edit-lead-button"
                >
                  Edit Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Lead Information</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateLead} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={editData.first_name || ''}
                        onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={editData.last_name || ''}
                        onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={editData.company_name || ''}
                      onChange={(e) => setEditData({...editData, company_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Business Type</Label>
                    <Input
                      value={editData.business_type || ''}
                      onChange={(e) => setEditData({...editData, business_type: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => setEditData({...editData, website: e.target.value})}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={editData.address || ''}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Input
                      value={editData.source || ''}
                      onChange={(e) => setEditData({...editData, source: e.target.value})}
                      placeholder="e.g., Referral, Website, Cold Call"
                    />
                  </div>
                  <div>
                    <Label>Social Media Links</Label>
                    <div className="space-y-2">
                      <Input
                        placeholder="LinkedIn URL"
                        value={editData.social_media?.linkedin || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), linkedin: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Twitter URL"
                        value={editData.social_media?.twitter || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), twitter: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Facebook URL"
                        value={editData.social_media?.facebook || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), facebook: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Instagram URL"
                        value={editData.social_media?.instagram || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), instagram: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="TikTok URL"
                        value={editData.social_media?.tiktok || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), tiktok: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Podcast URL"
                        value={editData.social_media?.podcast || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), podcast: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Custom Link URL"
                        value={editData.social_media?.custom_link || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), custom_link: e.target.value}
                        })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    Save Changes
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {leadData.lead.status !== 'converted' && (
              <Button
                onClick={handleConvertToClient}
                data-testid="convert-to-client-button"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Convert to Client
              </Button>
            )}

            <input
              type="file"
              id="file-upload-lead"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <label 
              htmlFor="file-upload-lead"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-md cursor-pointer font-medium text-sm transition-all"
              data-testid="upload-document-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Document'}
            </label>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <Label>File</Label>
              <p className="text-sm text-gray-600 mt-1">{uploadData.file?.name}</p>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {predefinedTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      if (uploadData.tags.includes(tag)) {
                        setUploadData({...uploadData, tags: uploadData.tags.filter(t => t !== tag)});
                      } else {
                        setUploadData({...uploadData, tags: [...uploadData.tags, tag]});
                      }
                    }}
                    className={`px-3 py-1 text-sm rounded-full ${
                      uploadData.tags.includes(tag) 
                        ? 'bg-teal-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="customTag">Custom Tag (Optional)</Label>
              <Input
                id="customTag"
                value={uploadData.customTag}
                onChange={(e) => setUploadData({...uploadData, customTag: e.target.value})}
                placeholder="Enter custom tag"
              />
            </div>

            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={uploading}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={(open) => {
        setPreviewDialogOpen(open);
        if (!open && previewUrl) {
          // Only revoke blob URLs
          if (previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewUrl('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewFile?.filename}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {previewFile && (
              <>
                {previewFile.mime_type.startsWith('image/') && previewUrl ? (
                  <img 
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="w-full h-auto"
                  />
                ) : previewFile.mime_type === 'application/pdf' && previewUrl ? (
                  <div className="w-full h-[650px]">
                    <PDFViewer 
                      url={previewUrl}
                      filename={previewFile.filename}
                      onDownload={() => handleDownload(previewFile.id, previewFile.filename)}
                    />
                  </div>
                ) : previewFile.mime_type === 'text/plain' ? (
                  <div className="w-full h-[600px] overflow-auto bg-gray-50 p-6">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                      {textContent || 'Loading...'}
                    </pre>
                  </div>
                ) : previewFile.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                     previewFile.mime_type === 'application/msword' ||
                     previewFile.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     previewFile.mime_type === 'application/vnd.ms-excel' ? (
                  <div className="w-full h-[600px] overflow-auto">
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Office documents cannot be previewed in-browser</p>
                      <p className="text-sm text-gray-400 mb-4">Download the file to view it</p>
                      <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download {previewFile.filename}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                    <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <StickyNote className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{leadData.stats.notes_count}</p>
              <p className="text-sm text-gray-600">Notes</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{leadData.stats.documents_count}</p>
              <p className="text-sm text-gray-600">Documents</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Lead Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <User className="w-4 h-4 mr-2" />
                    Full Name
                  </label>
                  <p className="text-gray-900 font-medium">
                    {leadData.lead.first_name} {leadData.lead.last_name}
                  </p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </label>
                  <p className="text-gray-900 font-medium">{leadData.lead.email}</p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Phone className="w-4 h-4 mr-2" />
                    Phone
                  </label>
                  <p className="text-gray-900 font-medium">{leadData.lead.phone || 'Not provided'}</p>
                </div>
                {leadData.lead.website && (
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </label>
                    <a href={leadData.lead.website} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">
                      {leadData.lead.website}
                    </a>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Building className="w-4 h-4 mr-2" />
                    Company
                  </label>
                  <p className="text-gray-900 font-medium">{leadData.lead.company_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Building className="w-4 h-4 mr-2" />
                    Business Type
                  </label>
                  <p className="text-gray-900 font-medium">{leadData.lead.business_type || 'N/A'}</p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <FileText className="w-4 h-4 mr-2" />
                    Source
                  </label>
                  <p className="text-gray-900 font-medium capitalize">{leadData.lead.source ? leadData.lead.source.replace('_', ' ') : 'N/A'}</p>
                </div>
                {leadData.lead.address && (
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      Address
                    </label>
                    <p className="text-gray-900 font-medium">{leadData.lead.address}</p>
                  </div>
                )}
              </div>
            </div>

            {leadData.lead.social_media && Object.keys(leadData.lead.social_media).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Social Media</h4>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(leadData.lead.social_media).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {getSocialIcon(platform)}
                      <span className="text-sm text-gray-700 capitalize">{platform}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leadData.documents.length === 0 ? (
              <div className="col-span-full glass rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No documents yet. Upload documents for this lead.</p>
              </div>
            ) : (
              leadData.documents.map((doc, index) => (
                <div 
                  key={doc.id} 
                  className="glass rounded-xl p-4 hover:shadow-lg transition-shadow cursor-pointer" 
                  data-testid={`document-${index}`}
                  onClick={() => handlePreview(doc)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(doc.id, doc.filename)}
                        className="h-8 w-8 p-0"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1 truncate" title={doc.filename}>
                    {doc.filename}
                  </h4>
                  <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </p>
                  {doc.tags && doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  data-testid="add-note-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Lead Note</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddNote} className="space-y-4">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note..."
                    rows={4}
                    required
                    data-testid="note-input"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  >
                    Save Note
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {leadData.notes.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center">
                <StickyNote className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No notes yet. Add your first note about this lead.</p>
              </div>
            ) : (
              leadData.notes.map((note, index) => (
                <div key={note.id} className="glass rounded-xl p-4" data-testid={`note-${index}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <StickyNote className="w-4 h-4 text-teal-600" />
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteNote(note.id)}
                      className="h-6 w-6 p-0 text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{note.note}</p>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadDetails;
